import chroma from "chroma-js";
import { GlobalColor } from "@/types/colors";
import { NamedColorPalette } from "@/types/singularity/brand";
import { ButtonColors } from "./types";

/**
 * Minimum contrast ratios for WCAG compliance
 */
const MIN_TEXT_CONTRAST = 7.0; // WCAG AAA
const MIN_BUTTON_CONTRAST = 1.5;

/**
 * Extract shade number from a color label (e.g., "primary 300" -> 300)
 */
function extractShade(color: GlobalColor): number | null {
  const match = color.label?.match(/\b(\d{3})\b/);
  return match ? parseInt(match[1]) : null;
}

/**
 * Check if background is dark enough for shade 500 to have good contrast
 * @param backgroundColor The background color to check
 * @returns true if background is very dark (luminance < 0.2)
 */
function backgroundWillMake500LookGood(_backgroundColor: GlobalColor, bgShade: number): boolean {

  if (bgShade === 500) return false;

  if (bgShade === 900) return true;

  return false;

  // the below isn't good enough, a designer's brain has more inputs and is better...
  // if (!backgroundColor.value) {
  //   throw new Error('backgroundColor.value missing');
  // }

  // const bgValue = backgroundColor.value.toString();
  // const bgLuminance = chroma(bgValue).luminance();

  // return bgLuminance < 0.05;
}

/**
 * Get the background color for a color scheme
 * This is straightforward - just return the color
 *
 * @param color The background color
 * @returns The background color
 */
export function getColorForBackground(color: GlobalColor): GlobalColor {
  return color;
}

/**
 * Get a text color with enough contrast from the background
 * Searches through the palette to find a color with at least 7:1 contrast (WCAG AAA)
 * Skips banned combinations automatically
 *
 * @param backgroundColor The background color
 * @param palette The palette to search for text colors
 * @returns A color with sufficient contrast for text
 */
export function getColorWithEnoughContrastFromBackgroundForText(
  backgroundColor: GlobalColor,
  palette: NamedColorPalette
): GlobalColor {
  if (!backgroundColor.value) {
    throw new Error('Background color value is undefined');
  }

  const bgValue = backgroundColor.value.toString();
  const bgLuminance = chroma(bgValue).luminance();
  const bgShade = extractShade(backgroundColor);

  // Determine search order based on background luminance
  const shadeOrder = bgLuminance >= 0.5
    ? [900, 800, 700, 600, 500, 400, 300, 200, 100] // Light bg -> search dark first
    : [100, 200, 300, 400, 500, 600, 700, 800, 900]; // Dark bg -> search light first

  for (const shade of shadeOrder) {
    const candidate = palette.scale[shade as keyof typeof palette.scale];
    if (!candidate) continue;

    if (!candidate.value) {
      throw new Error(`Candidate color value is undefined for shade ${shade}`);
    }

    const candidateValue = candidate.value.toString();
    const contrast = chroma.contrast(bgValue, candidateValue);

    // Check if has enough contrast
    if (contrast >= MIN_TEXT_CONTRAST) {
      return candidate;
    }
  }

  // Fallback: return the extreme shade
  return bgLuminance >= 0.5 ? palette.scale[900] : palette.scale[100];
}

export function getButtonColors(
  backgroundColor: GlobalColor,
  palette: NamedColorPalette,
  type: 'primary' | 'neutral' | 'secondary',
  primaryPalette: NamedColorPalette
): ButtonColors {
  if (!backgroundColor.value) {
    throw new Error('Background color value is undefined');
  }

  const bgShade = extractShade(backgroundColor);

  if (!bgShade) {
    throw new Error('could not extract shade from backgroundColor');
  }

  const buttonBackgroundIsTextColorAndButtonTextIsBackgroundColor = () => {
    const buttonColor = getColorWithEnoughContrastFromBackgroundForText(backgroundColor, palette);

    return {
      bgNormal: buttonColor,
      bgHover: buttonColor,
      textNormal: backgroundColor,
      textHover: backgroundColor
    };
  }

  if (type === 'neutral') {
    if ([100, 900].includes(bgShade)) {
      return {
        bgNormal: primaryPalette.scale[500],
        bgHover: primaryPalette.scale[600],
        textNormal: primaryPalette.text,
        textHover: primaryPalette.text
      };
    } else {
      return buttonBackgroundIsTextColorAndButtonTextIsBackgroundColor();
    }
  } else if (type === 'primary') {
    if ([100, 200, 300].includes(bgShade) || backgroundWillMake500LookGood(backgroundColor, bgShade)) {
      return {
        bgNormal: palette.scale[500],
        bgHover: palette.scale[600],
        textNormal: palette.text,
        textHover: palette.text
      };
    } else {
      return buttonBackgroundIsTextColorAndButtonTextIsBackgroundColor();
    }
  } else { // type === 'secondary'
    return buttonBackgroundIsTextColorAndButtonTextIsBackgroundColor();
  }

}
